import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/commerce-php/commerce-php/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "extension-lifecycle"
    }}>{`Extension lifecycle`}</h1>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`The recommendations laid out below are to be phased out in the future in favor of `}<a parentName="p" {...{
        "href": "/commerce/php/development/components/declarative-schema/#declarative-schema"
      }}>{`declarative schema`}</a>{` development approaches.`}</p>
    <p>{`This topic describes a module's lifecycle and how to create classes that execute code when your module is initialized, upgraded, or uninstalled.
These executable classes can perform tasks that set up the database, update data, and clean up data.`}</p>
    <p><em parentName="p">{`Note:`}</em>{` Theme and language package extensions do not need initialization or uninstallation tasks because they do not install database schemas or update data.`}</p>
    <h2 {...{
      "id": "lifecycle-guidelines"
    }}>{`Lifecycle guidelines`}</h2>
    <p>{`Follow these guidelines when developing your executable classes to have them run during specific lifecycle stages:`}</p>
    <ul>
      <li parentName="ul">{`Put your executable class in the `}<inlineCode parentName="li">{`Setup`}</inlineCode>{` directory inside your module's root directory.`}</li>
      <li parentName="ul">{`Use the specific file and class name for your class's target lifecycle stage.`}</li>
      <li parentName="ul">{`Implement the specific class interface and function for your class's target stage.`}</li>
      <li parentName="ul">{`Follow Magento's `}<a parentName="li" {...{
          "href": "../versioning/index.md"
        }}>{`versioning policy`}</a>{` when changing your module's version.`}</li>
    </ul>
    <h2 {...{
      "id": "schema-initialization-stages"
    }}>{`Schema initialization stages`}</h2>
    <p>{`The schema initialization stages are the first set of processes that Adobe Commerce and Magento Open Source run when your module is installed, re-installed, or upgraded.`}</p>
    <h3 {...{
      "id": "schema-installation"
    }}>{`Schema installation`}</h3>
    <p>{`The application executes the schema installation class during your module's initial install.
If the `}<inlineCode parentName="p">{`schema_version`}</inlineCode>{` for your module is found in the `}<inlineCode parentName="p">{`setup_module`}</inlineCode>{` table, the application skips this stage and proceeds to the `}<a parentName="p" {...{
        "href": "#schema-upgrade"
      }}>{`schema upgrade`}</a>{` stage.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}></th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Class name:`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`InstallSchema`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Interface:`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/magento/magento2/blob/2.4/lib/internal/Magento/Framework/Setup/InstallSchemaInterface.php"
            }}><inlineCode parentName="a">{`InstallSchemaInterface`}</inlineCode></a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Method:`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`install()`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <p><strong parentName="p">{`Example:`}</strong>{` InstallSchema.php`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

namespace VendorName\\ModuleName\\Setup;

use Magento\\Framework\\Setup\\InstallSchemaInterface;

class InstallSchema implements InstallSchemaInterface
{
    /**
     * @inheritdoc
     */
    public function install(SchemaSetupInterface $setup, ModuleContextInterface $context)
    {
        //Install schema logic
    }
}
`}</code></pre>
    <h3 {...{
      "id": "schema-upgrade"
    }}>{`Schema upgrade`}</h3>
    <p>{`The application executes your module's schema upgrade class when it detects an earlier installation.
The purpose of this class is to update the database structure or apply patches.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}></th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Class name`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`UpgradeSchema`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Interface`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/magento/magento2/blob/2.4/lib/internal/Magento/Framework/Setup/UpgradeSchemaInterface.php"
            }}><inlineCode parentName="a">{`UpgradeSchemaInterface`}</inlineCode></a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Method`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`upgrade()`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <p><strong parentName="p">{`Example:`}</strong>{` UpgradeSchema.php`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

namespace VendorName\\ModuleName\\Setup;

use Magento\\Framework\\Setup\\UpgradeSchemaInterface;

class UpgradeSchema implements UpgradeSchemaInterface
{
    /**
     * @inheritdoc
     */
    public function upgrade(SchemaSetupInterface $setup, ModuleContextInterface $context)
    {
        //Upgrade schema logic
    }
}
`}</code></pre>
    <h3 {...{
      "id": "recurring-schema-event"
    }}>{`Recurring schema event`}</h3>
    <p>{`The application executes your module's recurring schema event class after every schema installation or upgrade stage.
This class makes final modifications to the database schema after it has been installed or updated.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}></th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Class name`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`Recurring`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Interface`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/magento/magento2/blob/2.4/lib/internal/Magento/Framework/Setup/InstallSchemaInterface.php"
            }}><inlineCode parentName="a">{`InstallSchemaInterface`}</inlineCode></a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Method`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`install()`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <p><strong parentName="p">{`Example:`}</strong>{` Recurring.php`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

namespace VendorName\\ModuleName\\Setup;

use Magento\\Framework\\Setup\\InstallSchemaInterface;

class Recurring implements InstallSchemaInterface
{
    /**
     * @inheritdoc
     */
    public function install(SchemaSetupInterface $setup, ModuleContextInterface $context)
    {
        //Recurring schema event logic
    }
}
`}</code></pre>
    <h2 {...{
      "id": "data-initialization"
    }}>{`Data initialization`}</h2>
    <p>{`The application goes through your module's data initialization stages after the schema initialization processes complete.`}</p>
    <h3 {...{
      "id": "data-installation"
    }}>{`Data installation`}</h3>
    <p>{`The application executes the data installation class during your module's initial install unless an existing version entry is found in the database.
The purpose of this class is to populate the database with initial data.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}></th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Class name`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`InstallData`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Interface`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/magento/magento2/blob/2.4/lib/internal/Magento/Framework/Setup/InstallDataInterface.php"
            }}><inlineCode parentName="a">{`InstallDataInterface`}</inlineCode></a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Method`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`install()`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <p><strong parentName="p">{`Example:`}</strong>{` InstallData.php`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

namespace VendorName\\ModuleName\\Setup;

use Magento\\Framework\\Setup\\InstallDataInterface;

class InstallData implements InstallDataInterface
{
    /**
     * {@inheritdoc}
     */
    public function install(ModuleDataSetupInterface $setup, ModuleContextInterface $context)
    {
        // Data install logic
    }
}
`}</code></pre>
    <h3 {...{
      "id": "data-upgrade"
    }}>{`Data upgrade`}</h3>
    <p>{`The application executes the data upgrade class when it detects an earlier version in the `}<inlineCode parentName="p">{`data_version`}</inlineCode>{` field for the module in the `}<inlineCode parentName="p">{`setup_module`}</inlineCode>{` table.
The purpose of this class is to fix corrupted data or populate a new data field after a schema change.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}></th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Class name`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`UpgradeData`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Interface`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/magento/magento2/blob/2.4/lib/internal/Magento/Framework/Setup/UpgradeDataInterface.php"
            }}><inlineCode parentName="a">{`UpgradeDataInterface`}</inlineCode></a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Method`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`upgrade()`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <p><strong parentName="p">{`Example:`}</strong>{` UpgradeData.php`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

namespace VendorName\\ModuleName\\Setup;

use Magento\\Framework\\Setup\\UpgradeDataInterface;

class UpgradeData implements UpgradeDataInterface
{
    /**
     * @inheritdoc
     */
    public function upgrade(ModuleDataSetupInterface $setup, ModuleContextInterface $context)
    {
        // Data upgrade logic
    }
}
`}</code></pre>
    <h3 {...{
      "id": "recurring-data-event"
    }}>{`Recurring data event`}</h3>
    <p>{`The application executes your module's recurring data event class after every data installation or upgrade stage.
This class makes final modifications to the database store after data has been installed or updated.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}></th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Class name`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`RecurringData`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Interface`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/magento/magento2/blob/2.4/lib/internal/Magento/Framework/Setup/InstallDataInterface.php"
            }}><inlineCode parentName="a">{`InstallDataInterface`}</inlineCode></a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Method`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`install()`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <p><strong parentName="p">{`Example:`}</strong>{` RecurringData.php`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

namespace VendorName\\ModuleName\\Setup;

use Magento\\Framework\\Setup\\InstallDataInterface;

class RecurringData implements InstallDataInterface
{
    /**
     * @inheritdoc
     */
    public function install(ModuleDataSetupInterface $setup, ModuleContextInterface $context)
    {
        // Recurring data event logic
    }
}
`}</code></pre>
    <h2 {...{
      "id": "database-interface"
    }}>{`Database interface`}</h2>
    <p>{`Use the `}<a parentName="p" {...{
        "href": "https://github.com/magento/magento2/blob/2.4/lib/internal/Magento/Framework/Setup/ModuleDataSetupInterface.php"
      }}><inlineCode parentName="a">{`ModuleDataSetupInterface`}</inlineCode></a>{` when you need to do database manipulations.
If your installation or upgrade logic spans multiple classes, pass this resource on to other classes that need to modify the database.`}</p>
    <p><strong parentName="p">{`Example:`}</strong>{` `}<a parentName="p" {...{
        "href": "https://github.com/magento/magento2/blob/2.4/app/code/Magento/Customer/Setup/Patch/Data/DefaultCustomerGroupsAndAttributes.php"
      }}>{`Customer module's DefaultCustomerGroupsAndAttributes.php`}</a></p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

class DefaultCustomerGroupsAndAttributes implements DataPatchInterface, PatchVersionInterface
{
    /**
     * @var CustomerSetupFactory
     */
    private $customerSetupFactory;
    /**
     * @var ModuleDataSetupInterface
     */
    private $moduleDataSetup;
    /**
     * DefaultCustomerGroupsAndAttributes constructor.
     * @param CustomerSetupFactory $customerSetupFactory
     * @param ModuleDataSetupInterface $moduleDataSetup
     */
    public function __construct(
        CustomerSetupFactory $customerSetupFactory,
        ModuleDataSetupInterface $moduleDataSetup
    ) {
        $this->customerSetupFactory = $customerSetupFactory;
        $this->moduleDataSetup = $moduleDataSetup;
    }
    /**
     * {@inheritdoc}
     * @SuppressWarnings(PHPMD.ExcessiveMethodLength)
     */
    public function apply()
    {
        /** @var CustomerSetup $customerSetup */
        $customerSetup = $this->customerSetupFactory->create(['setup' => $this->moduleDataSetup]);
        ...
        $customerSetup->installEntities();
        $customerSetup->installCustomerForms();
        $disableAGCAttribute = $customerSetup->getEavConfig()->getAttribute('customer', 'disable_auto_group_change');
        ...
        $migrationSetup = $this->moduleDataSetup->createMigrationSetup();
        $migrationSetup->appendClassAliasReplace(
            'customer_eav_attribute',
            'data_model',
            Migration::ENTITY_TYPE_MODEL,
            Migration::FIELD_CONTENT_TYPE_PLAIN,
            ['attribute_id']
        );
        $migrationSetup->doUpdateClassAliases();
    }
    ...
}
`}</code></pre>
    <h2 {...{
      "id": "module-version"
    }}>{`Module version`}</h2>
    <p>{`Use the `}<a parentName="p" {...{
        "href": "https://github.com/magento/magento2/blob/2.4/lib/internal/Magento/Framework/Setup/ModuleContextInterface.php"
      }}><inlineCode parentName="a">{`ModuleContextInterface`}</inlineCode></a>{` to get the current module version and execute logic based on the version.`}</p>
    <p><strong parentName="p">{`Example:`}</strong>{` User module's UpgradeData.php`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

namespace Magento\\User\\Setup;

use Magento\\Framework\\Encryption\\Encryptor;
use Magento\\Framework\\Setup\\ModuleContextInterface;
use Magento\\Framework\\Setup\\ModuleDataSetupInterface;
use Magento\\Framework\\Setup\\UpgradeDataInterface;

class UpgradeData implements UpgradeDataInterface
{
    /**
     * @inheritdoc
     */
    public function upgrade(ModuleDataSetupInterface $setup, ModuleContextInterface $context)
    {
        $setup->startSetup();
        if (version_compare($context->getVersion(), '2.0.1', '<')) {
            $this->upgradeHash($setup);
        }
        $setup->endSetup();
    }

    ...

}
`}</code></pre>
    <h2 {...{
      "id": "uninstall-event"
    }}>{`Uninstall event`}</h2>
    <p>{`The application executes the uninstall event class when your module is uninstalled using the following command:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`bin/magento module:uninstall --remove-data <module_name>
`}</code></pre>
    <p>{`In this phase, your module should remove all traces of its existence in the database by dropping tables, deleting data, or restoring data.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}></th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Class name`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`Uninstall`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Interface`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/magento/magento2/blob/2.4/lib/internal/Magento/Framework/Setup/UninstallInterface.php"
            }}><inlineCode parentName="a">{`UninstallInterface`}</inlineCode></a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Method`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`uninstall()`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <p><strong parentName="p">{`Example:`}</strong>{` Uninstall.php`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

namespace VendorName\\ModuleName\\Setup;

use Magento\\Framework\\Setup\\UninstallInterface;

class Uninstall implements UninstallInterface
{
    /**
     * @inheritdoc
     */
    public function uninstall(SchemaSetupInterface $setup, ModuleContextInterface $context)
    {
        //Uninstall logic
    }
}
`}</code></pre>
    <h3 {...{
      "id": "disabled-modules"
    }}>{`Disabled modules`}</h3>
    <p>{`A disabled module can still execute its uninstall event.
However, module-specific configurations such as its dependency injection and event/observer configurations will not be available and will cause problems.`}</p>
    <p>{`Avoid this situation by not including dependencies in your uninstall event class`}</p>
    <p><strong parentName="p">{`Related Topics:`}</strong></p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../versioning/index.md"
        }}>{`Versioning policy`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      